/**
 * useAuth — hook de autenticação Azure AD via Supabase.
 *
 * Expõe: session, user, profile, loading, unauthorized, sessionExpired,
 * signIn() e signOut().
 *
 * Fluxo:
 *   1. signIn() → Supabase redireciona para Azure AD (PKCE)
 *   2. Callback OAuth → Supabase cria/atualiza auth.users
 *   3. Trigger DB vincula auth.user ao engineer pelo email
 *   4. useEffect separado busca engineers.profile quando session muda
 *
 * Perfis:
 *   'operador' — Vigilante (leitura + inserção de readings)
 *   'tecnico_responsavel' — Engenheiro (tudo + fecha TARP)
 *   'admin' — Admin JFB (gerencia usuários)
 */

import { useEffect, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { eventosApp } from '@/services/datasources/eventos-app';
import type { OrigemLogout } from '@/domain/auditoria/eventos-app';

export type UserProfile = 'operador' | 'tecnico_responsavel' | 'admin';

interface AuthState {
  session: Session | null;
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;        // true até INITIAL_SESSION ser processado
  profileLoading: boolean; // true enquanto busca perfil no banco
  unauthorized: boolean;
  sessionExpired: boolean;
}

/**
 * Resultado de uma tentativa de logout. Existe porque `signOut` pode falhar
 * **sem encerrar a sessão** — ver o comentário em `signOut()`. Quem chama tem
 * de saber, para não dizer ao usuário que ele saiu quando não saiu.
 */
export interface SignOutResult {
  error: Error | null;
}

interface UseAuthReturn extends AuthState {
  signIn: () => Promise<void>;
  /** `origem` vai para a trilha (GUPM-335): de onde partiu o "Sair". */
  signOut: (origem?: OrigemLogout) => Promise<SignOutResult>;
}

export function useAuth(): UseAuthReturn {
  const [state, setState] = useState<AuthState>({
    session: null,
    user: null,
    profile: null,
    loading: true,
    profileLoading: false,
    unauthorized: false,
    sessionExpired: false,
  });

  // Passo 1: ouvir mudanças de auth — só atualiza session/user, não faz query
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (
          event === 'INITIAL_SESSION' ||
          event === 'SIGNED_IN' ||
          event === 'TOKEN_REFRESHED' ||
          event === 'USER_UPDATED'
        ) {
          setState(prev => {
            // TOKEN_REFRESHED dispara com o mesmo user.id — não rebuscar perfil
            // já carregado, pois o useEffect de perfil depende de user.id e não
            // re-executaria, deixando profileLoading preso em true.
            const isNewUser = session != null && prev.user?.id !== session.user.id;
            return {
              ...prev,
              session,
              user: session?.user ?? null,
              loading: false,
              sessionExpired: false,
              // Limpa perfil anterior ao trocar de usuário
              profile: session ? prev.profile : null,
              // Mesmo usuário: mantém o estado da busca em curso. O guardião da
              // sessão (GUPM-336) renova o token logo na abertura, com o perfil
              // ainda em voo; zerar aqui fazia o RoleGuard ler "carregado, sem
              // perfil" e expulsar o RT de /auditoria no recarregamento (C-68).
              profileLoading: isNewUser || (session != null && prev.profileLoading),
            };
          });
        } else if (event === 'SIGNED_OUT') {
          setState({
            session: null,
            user: null,
            profile: null,
            loading: false,
            profileLoading: false,
            unauthorized: false,
            sessionExpired: false,
          });
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Passo 2: quando session muda e tem user, busca o perfil no banco
  // Separado do onAuthStateChange para garantir que o JWT está injetado no client
  useEffect(() => {
    if (!state.user || !state.session) return;

    let cancelled = false;

    async function loadProfile(user: User) {
      try {
        const { data, error } = await supabase
          .from('app_users_public')
          .select('profile')
          .eq('auth_user_id', user.id)
          .single();

        if (cancelled) return;

        if (error || !data) {
          setState(prev => ({
            ...prev,
            profile: null,
            profileLoading: false,
            unauthorized: true,
          }));
        } else {
          setState(prev => ({
            ...prev,
            profile: data.profile as UserProfile,
            profileLoading: false,
            unauthorized: false,
          }));
        }
      } catch {
        if (cancelled) return;
        setState(prev => ({ ...prev, profile: null, profileLoading: false, unauthorized: true }));
      }
    }

    loadProfile(state.user);

    return () => { cancelled = true; };
  // Intencional: re-busca perfil apenas quando o user.id muda (novo login).
  // Incluir state.session causaria re-fetch a cada TOKEN_REFRESHED.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.user?.id]);

  async function signIn() {
    await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        scopes: 'email profile openid',
        redirectTo: window.location.origin,
        // GUPM-255: sem `prompt`, o padrão do OpenID Connect é SSO silencioso —
        // havendo sessão da Microsoft no navegador, o Entra ID reautentica na
        // última conta e volta sem mostrar tela. Como `signOut()` só encerra a
        // sessão DO APP (o cookie em login.microsoftonline.com continua vivo),
        // quem saía ficava preso à conta anterior: trava no posto de vigilância,
        // onde o dispositivo é compartilhado entre plantonistas.
        //
        // `select_account` pede o seletor de contas. Não é `login` de propósito:
        // aquele forçaria senha + MFA a cada acesso, inviável em campo com 4G
        // instável — e o segundo fator que importa aqui é o TOTP das ações
        // sensíveis, que segue exigido.
        queryParams: { prompt: 'select_account' },
      },
    });
  }

  async function signOut(origem: OrigemLogout = 'menu'): Promise<SignOutResult> {
    // GUPM-335: o clique em "Sair" vai para a trilha ANTES do signOut — depois
    // dele o JWT não vale mais e a RPC não saberia quem saiu. O servidor grava
    // por conta própria `sessao_encerrada` quando o GoTrue revoga a sessão;
    // este evento acrescenta o que ele não sabe: que foi a PESSOA que saiu, e
    // de qual tela. `registrar` nunca lança e tem teto de espera.
    await eventosApp.registrar('logout', { origem });

    // Encerra apenas a sessão do app. Derrubar a sessão da Microsoft exigiria
    // redirecionar para o `end_session_endpoint` do Entra ID, o que deslogaria
    // também Outlook/Teams/SharePoint abertos no mesmo navegador. O
    // `select_account` do login cobre a troca de conta sem esse efeito colateral.
    //
    // GUPM-255: o erro é DEVOLVIDO, não descartado. Se o `POST /logout` falha
    // com algo que não seja 404/401/403, o auth-js retorna antes de limpar a
    // sessão: o token fica no localStorage, o refresh automático segue vivo e
    // nenhum `SIGNED_OUT` é emitido. Engolir esse erro fazia a UI afirmar uma
    // saída que não ocorreu — e no posto de vigilância, com o dispositivo
    // compartilhado, o plantonista seguinte agiria dentro da conta do anterior,
    // assinando o `audit_log` com o `user_id` errado.
    //
    // GUPM-334: `scope: 'local'` é obrigatório. Sem argumento o auth-js usa
    // `'global'`, que revoga TODAS as sessões do usuário — e o vigilante fica
    // logado com a mesma conta na sala de monitoramento e na portaria. O "Sair"
    // numa estação derrubava a outra na renovação seguinte, na troca de turno.
    // O `'local'` segue revogando no servidor a sessão DESTE dispositivo.
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) {
      // Sem PII: a mensagem do auth-js descreve o transporte, não o usuário.
      console.error('[useAuth] falha ao encerrar a sessão:', error.message);
    }
    return { error: error ?? null };
  }

  return { ...state, signIn, signOut };
}
